import * as React from 'react';
import { useFormContext, Controller } from 'react-hook-form';
import { Input, InputProps } from '../ui/Input';
import { Textarea, TextareaProps } from '../ui/Textarea';
import { Label } from '../ui/Label';
import { cn } from '../../lib/utils';

// ----------------------------------------------------------------------

type RHFTextFieldProps = Omit<InputProps, 'name' | 'size'> & {
  name: string;
  label?: string;
  helperText?: React.ReactNode;
  multiline?: boolean;
  rows?: number;
  minRows?: number;
  startAdornment?: React.ReactNode;
  endAdornment?: React.ReactNode;
};

export default function RHFTextField({
  name,
  label,
  helperText,
  className,
  multiline,
  rows,
  minRows,
  startAdornment,
  endAdornment,
  ...other
}: RHFTextFieldProps) {
  const { control } = useFormContext();

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState: { error } }) => (
        <div className="w-full space-y-1">
          {label && (
            <Label htmlFor={name} className={cn(error && 'text-destructive')}>
              {label}
            </Label>
          )}
          {multiline ? (
            <Textarea
              {...field}
              id={name}
              value={field.value ?? ''}
              error={!!error}
              rows={rows || minRows}
              className={cn('w-full', className)}
              {...(other as TextareaProps)}
            />
          ) : (
            <div className="relative flex items-center">
              {startAdornment && (
                <div className="absolute left-3 flex items-center text-muted-foreground">
                  {startAdornment}
                </div>
              )}
              <Input
                {...field}
                id={name}
                value={
                  typeof field.value === 'number' && field.value === 0 ? '' : field.value ?? ''
                }
                error={!!error}
                className={cn(
                  'w-full',
                  startAdornment && 'pl-10',
                  endAdornment && 'pr-10',
                  className
                )}
                {...other}
              />
              {endAdornment && (
                <div className="absolute right-3 flex items-center text-muted-foreground">
                  {endAdornment}
                </div>
              )}
            </div>
          )}
          {(error || helperText) && (
            <p className={cn('text-sm', error ? 'text-destructive' : 'text-muted-foreground')}>
              {error ? error.message : helperText}
            </p>
          )}
        </div>
      )}
    />
  );
}
